import React, { ChangeEvent, HTMLInputTypeAttribute } from 'react';
import { TextField, InputAdornment, IconButton, TextFieldProps, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@emotion/styled';
import { formatInputAsCurrency, isValidCurrencyString } from 'utils/helpers';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface TextInputProps extends Omit<TextFieldProps, 'name' | 'defaultValues'> {
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    width?: string | number;
    height?: string | number;
    multiline?: boolean;
    minRows?: number;
    backgroundColor?: string; // Add backgroundColor prop
    value?:any;
    name?: string;
    setValue?: (name?:any, value?:any) => void;
    type?: HTMLInputTypeAttribute | 'currency';
    adornmentMargin?: number;
    fieldDescription?: string;
}

const PrimaryTextInput = styled(TextField)(({ width, height, multiline, backgroundColor }: TextInputProps) => ({
    backgroundColor: backgroundColor || 'white', // Use the provided backgroundColor or default to 'white'
    width: width,
    height: height,
    '& .MuiInputBase-input': {
        height: height,
        padding: `0 2px 2px ${multiline ? 2 : 12}px`,
    },
}));

const TextInput: React.FC<TextInputProps> = ({
    icon = <SearchIcon />,
    iconPosition = 'start',
    width,
    height = 50,
    placeholder,
    sx = {},
    label,
    onChange,
    setValue,
    name,
    multiline,
    minRows,
    type = 'text',
    value,
    backgroundColor = 'white',
    adornmentMargin, // Default backgroundColor to 'white'
    fieldDescription,
    helperText,
    ...props
}) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handelChangetext = (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            if (type === 'currency') {
                // CHECKING IF THE USER PRESSED ANY KEY EXCEPT NUMBER THEN WE ARE NOT FIRING THE ON CHANGE EVENT.
                // else we are converting the number to cents
                const newValue = event.target.value?.replace(/[- #*;,.<>\{\}\[\]\\\/]/gi, '').replace('$', '');
                if (!isValidCurrencyString(newValue)) {
                    return;
                }
                event.target.value = newValue;
            }
            return onChange(event);
        }
        if (event.target.type === 'number') {
            setValue && setValue(event.target.name, parseFloat(event.target.value));
        } else {
            setValue && setValue(event.target.name, event.target.value);
        }
    };

    const adornment = (icon || label) && (
        <InputAdornment position={iconPosition}>
            {icon ? (
                <IconButton sx={{ color: '#202A44' }}>{icon}</IconButton>
            ) : (
                <Typography mt={adornmentMargin} sx={{ fortWeight: '400', color: 'black' }}>
                    {label}:
                </Typography>
            )}
        </InputAdornment>
    );

    return (
        <PrimaryTextInput
            width={width || '100%'}
            height={height}
            sx={sx}
            name={name}
            placeholder={placeholder}
            type={showPassword ? 'text' : type}
            value={type === 'currency' ? formatInputAsCurrency(value) : value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handelChangetext(event)}
            multiline={multiline}
            minRows={minRows}
            backgroundColor={backgroundColor}
            InputProps={{
                endAdornment: type === 'password' && (
                    <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                    </InputAdornment>
                ),
                [`${iconPosition}Adornment`]: adornment,
            }}
            helperText={
                helperText ||
                (fieldDescription && (
                    <Typography fontSize={'18px'} color={'black'}>
                        {fieldDescription}
                    </Typography>
                ))
            }
            {...props}
        />
    );
};

export default TextInput;
